import {
  DirectionsBike as BikeIcon,
  Event as EventIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import moment, { type Moment } from "moment";
import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  getReservationsByFleet,
  getVehiclesList,
  type ReservationWithResolution,
  type VehicleListItem,
} from "../../apis/reservations/apis";
import { getUser } from "../../apis/user/user-apis";
import { DASHBOARD_BIKE, DASHBOARD_USER } from "../../constants/routes";
import type { User } from "../../domain/user";

interface ReservationsListProps {
  fleetId: string;
  currency: string | null;
}

const statusColors: Record<
  string,
  "success" | "warning" | "error" | "info" | "default"
> = {
  ACTIVE: "info",
  COMPLETED: "success",
  CANCELED: "default",
  PAYMENT_FAILED: "error",
};

const statusLabels: Record<string, string> = {
  ACTIVE: "Active",
  COMPLETED: "Completed",
  CANCELED: "Canceled",
  PAYMENT_FAILED: "Payment Failed",
};

const depositStatusColors: Record<
  string,
  "success" | "warning" | "info" | "default"
> = {
  authorized: "warning",
  captured: "success",
  released: "info",
};

const depositStatusLabels: Record<string, string> = {
  authorized: "Authorized",
  captured: "Captured",
  released: "Released",
};

const formatAmount = (amountMinor: number): string => {
  return (amountMinor / 100).toFixed(2);
};

const formatDateTime = (dateString: string): string => {
  return moment(dateString).format("MMM D, YYYY HH:mm");
};

export const ReservationsList: React.FC<ReservationsListProps> = ({
  fleetId,
  currency,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reservations, setReservations] = useState<ReservationWithResolution[]>(
    []
  );
  const [vehicles, setVehicles] = useState<VehicleListItem[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [dateFrom, setDateFrom] = useState<Moment | null>(
    moment().subtract(7, "days")
  );
  const [dateTo, setDateTo] = useState<Moment | null>(moment().add(1, "days"));
  const [userSearch, setUserSearch] = useState("");
  const [vehicleSearch, setVehicleSearch] = useState("");
  const [usersMap, setUsersMap] = useState<Map<number, User>>(new Map());

  const vehicleNameMap = useMemo(() => {
    const map = new Map<number, string>();
    vehicles.forEach((v) => map.set(v.id, v.name));
    return map;
  }, [vehicles]);

  const allUserIds = useMemo(() => {
    const ids = new Set<number>();
    reservations.forEach((r) => {
      if (r.legacy_user_id) {
        ids.add(r.legacy_user_id);
      }
    });
    return Array.from(ids);
  }, [reservations]);

  useEffect(() => {
    const fetchUsers = async () => {
      const newUsersMap = new Map(usersMap);
      const idsToFetch = allUserIds.filter((id) => !newUsersMap.has(id));

      for (const id of idsToFetch.slice(0, 50)) {
        try {
          const user = await getUser(id);
          newUsersMap.set(id, user);
        } catch (e) {
          // User not found, skip
        }
      }

      if (idsToFetch.length > 0) {
        setUsersMap(newUsersMap);
      }
    };

    if (allUserIds.length > 0) {
      fetchUsers();
    }
  }, [allUserIds]);

  const getUserDisplay = (userId: number | null | undefined) => {
    if (!userId) return null;
    const user = usersMap.get(userId);
    if (user) {
      return user.fullName || user.email || user.phone || `User ${userId}`;
    }
    return `User ${userId}`;
  };

  const fetchReservations = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getReservationsByFleet({
        fleet_id: fleetId,
        start_datetime_from: dateFrom?.toISOString(),
        start_datetime_to: dateTo?.toISOString(),
        status: statusFilter !== "all" ? statusFilter : undefined,
      });
      setReservations(data);
    } catch (e) {
      setError("Failed to load reservations");
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [fleetId, dateFrom, dateTo, statusFilter]);

  useEffect(() => {
    fetchReservations();
  }, [fetchReservations]);

  useEffect(() => {
    getVehiclesList().then(setVehicles).catch(console.error);
  }, []);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const sortedReservations = useMemo(() => {
    let filtered = reservations;

    if (userSearch.trim()) {
      const search = userSearch.trim().toLowerCase();
      filtered = filtered.filter(
        (r) =>
          r.user_id.toLowerCase().includes(search) ||
          r.legacy_user_id?.toString().includes(search)
      );
    }

    if (vehicleSearch.trim()) {
      const search = vehicleSearch.trim().toLowerCase();
      filtered = filtered.filter((r) => {
        const vehicleId = r.selector.legacy_vehicle_id;
        const vehicleName = vehicleId ? vehicleNameMap.get(vehicleId) : null;
        return (
          vehicleId?.toString().includes(search) ||
          vehicleName?.toLowerCase().includes(search) ||
          r.selector.vehicle_id?.toLowerCase().includes(search)
        );
      });
    }

    return [...filtered].sort(
      (a, b) =>
        new Date(b.start_datetime).getTime() -
        new Date(a.start_datetime).getTime()
    );
  }, [reservations, userSearch, vehicleSearch, vehicleNameMap]);

  const paginatedReservations = sortedReservations.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Paper sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Typography variant="h6">Reservations</Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchReservations} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <DatePicker
          label="From"
          value={dateFrom}
          onChange={setDateFrom}
          slotProps={{ textField: { size: "small", sx: { width: 160 } } }}
        />
        <DatePicker
          label="To"
          value={dateTo}
          onChange={setDateTo}
          slotProps={{ textField: { size: "small", sx: { width: 160 } } }}
        />
        <TextField
          select={true}
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          size="small"
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="all">All Statuses</MenuItem>
          <MenuItem value="ACTIVE">Active</MenuItem>
          <MenuItem value="COMPLETED">Completed</MenuItem>
          <MenuItem value="CANCELED">Canceled</MenuItem>
          <MenuItem value="PAYMENT_FAILED">Payment Failed</MenuItem>
        </TextField>
        <TextField
          label="Search User"
          value={userSearch}
          onChange={(e) => {
            setUserSearch(e.target.value);
            setPage(0);
          }}
          size="small"
          placeholder="User ID"
          sx={{ width: 150 }}
        />
        <TextField
          label="Search Vehicle"
          value={vehicleSearch}
          onChange={(e) => {
            setVehicleSearch(e.target.value);
            setPage(0);
          }}
          size="small"
          placeholder="ID or name"
          sx={{ width: 150 }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : sortedReservations.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography color="text.secondary">
            No reservations found for this time period
          </Typography>
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Reserved For</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Vehicle</TableCell>
                  <TableCell>Deposit</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedReservations.map((reservation) => (
                  <TableRow key={reservation.id} hover={true}>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <EventIcon fontSize="small" color="action" />
                        {formatDateTime(reservation.start_datetime)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDateTime(reservation.creation_datetime)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <PersonIcon fontSize="small" color="action" />
                        <Link
                          component={RouterLink}
                          to={DASHBOARD_USER.replace(
                            ":id",
                            String(
                              reservation.legacy_user_id || reservation.user_id
                            )
                          )}
                          underline="hover"
                        >
                          {getUserDisplay(reservation.legacy_user_id) ||
                            reservation.user_id.slice(0, 8)}
                        </Link>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <BikeIcon fontSize="small" color="action" />
                        {reservation.selector.legacy_vehicle_id ? (
                          <Link
                            component={RouterLink}
                            to={DASHBOARD_BIKE.replace(
                              ":id",
                              String(reservation.selector.legacy_vehicle_id)
                            )}
                            underline="hover"
                          >
                            {vehicleNameMap.get(
                              reservation.selector.legacy_vehicle_id
                            ) || reservation.selector.legacy_vehicle_id}
                          </Link>
                        ) : reservation.selector.vehicle_id ? (
                          <Link
                            component={RouterLink}
                            to={DASHBOARD_BIKE.replace(
                              ":id",
                              reservation.selector.vehicle_id
                            )}
                            underline="hover"
                          >
                            {reservation.selector.vehicle_id.slice(0, 8)}
                          </Link>
                        ) : (
                          "-"
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {reservation.deposit ? (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Typography variant="body2">
                            {formatAmount(reservation.deposit.amount_minor)}
                            {currency ? ` ${currency.toUpperCase()}` : ""}
                          </Typography>
                          {reservation.deposit.status && (
                            <Chip
                              label={
                                depositStatusLabels[
                                  reservation.deposit.status
                                ] || reservation.deposit.status
                              }
                              size="small"
                              color={
                                depositStatusColors[
                                  reservation.deposit.status
                                ] || "default"
                              }
                            />
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          statusLabels[reservation.status] || reservation.status
                        }
                        size="small"
                        color={statusColors[reservation.status] || "default"}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={sortedReservations.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </>
      )}
    </Paper>
  );
};
